import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MapPin, Truck, Shield, Star, ArrowRight, Sparkles, Zap, Heart, Truck as TruckIcon } from 'lucide-react';

const features = [
  { icon: Zap, label: 'Ultra Rapide', desc: 'Livreur chez vous en <strong>moins de 15 min</strong>', color: 'bg-amber-100 text-amber-600', iconColor: 'text-amber-600' },
  { icon: TruckIcon, label: 'Tout livrer', desc: 'Repas, colis, courses, documents - <strong>sans limite</strong>', color: 'bg-blue-100 text-blue-600', iconColor: 'text-blue-600' },
  { icon: Shield, title: '100% Sécurisé', desc: 'Paiement à la livraison, agents vérifiés, <strong>suivi GPS</strong>', color: 'bg-green-100 text-green-600', iconColor: 'text-green-600' },
  { icon: Star, title: 'Prix juste', desc: 'Tarif fixe connu à l\'avance, <strong>pas de surprise</strong>', color: 'bg-purple-100 text-purple-600', iconColor: 'text-purple-600' },
];

const steps = [
  { number: '1', title: 'Commandez', desc: 'Choisissez type, adresses, validez' },
  { number: '2', title: 'Suivez', desc: 'Agent assigné, temps réel sur carte' },
  { number: '3', title: 'Recevez', desc: 'Livré à votre porte, notez l\'agent' },
];

const trustItems = [
  { icon: Shield, label: 'Agents vérifiés', desc: 'Formation + documents' },
  { icon: MapPin, label: 'Suivi GPS', desc: 'Temps réel' },
  { icon: Heart, label: 'Support 24/7', desc: 'Équipe locale' },
  { icon: Star, label: '4.9/5', desc: 'Note clients' },
];

export default function Home() {
  const { user } = useAuth();
  const firstName = user?.fullName?.split(' ')[0] || 'Client';

  return (
    <div className="page-transition">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5" />
        <div className="container relative py-16 md:py-24 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6 animate-fade-in">
            <Sparkles size={16} />
            Nouveau : Livraison express 15 min
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-text mb-4 animate-slide-up">
            Bienvenue, <span className="gradient-text">{firstName}</span> !
          </h1>
          <p className="text-lg text-text-secondary max-w-xl mx-auto mb-10 animate-slide-up" style={{animationDelay: '100ms'}}>
            Besoin d'une livraison rapide à Ouagadougou ? Commandez en un clic et un livreur arrive en moins de 15 minutes.
          </p>
          <Link to="/new" className="btn btn-primary btn-lg px-10 animate-slide-up" style={{animationDelay: '200ms'}}>
            Nouvelle livraison <ArrowRight size={20} />
          </Link>
        </div>
        
        {/* Floating decorative elements */}
        <div className="absolute top-20 right-10 w-32 h-32 bg-primary/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 left-10 w-24 h-24 bg-secondary/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
      </section>

      {/* Features */}
      <section className="container px-4 py-16 md:py-24">
        <div className="text-center mb-12 animate-fade-in">
          <h2 className="text-2xl md:text-3xl font-bold text-text mb-4">Pourquoi choisir OuagaLiv ?</h2>
          <p className="text-text-secondary max-w-xl mx-auto">La livraison la plus rapide et fiable de Ouagadougou</p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div 
                key={index} 
                className="card group p-6 text-center h-full transition-all duration-300 hover:shadow-xl hover:-translate-y-1 animate-slide-up"
                style={{animationDelay: `${index * 100}ms`}}
              >
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-transform duration-300 group-hover:scale-110 ${feature.color}`}>
                  <Icon size={28} className={feature.iconColor} />
                </div>
                <h3 className="font-bold text-text mb-2 text-lg">{feature.label || feature.title}</h3>
                <p className="text-text-secondary text-sm leading-relaxed">{feature.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white py-16 md:py-24">
        <div className="container">
          <div className="text-center mb-12 animate-fade-in">
            <h2 className="text-2xl md:text-3xl font-bold text-text mb-4">Comment ça marche en 3 étapes</h2>
            <p className="text-text-secondary max-w-xl mx-auto">Simple, rapide, transparent</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative text-center animate-slide-up" style={{animationDelay: `${index * 150}ms`}}>
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-primary-light flex items-center justify-center mx-auto mb-6 text-white font-bold text-3xl shadow-lg">
                  {step.number}
                </div>
                <h3 className="text-xl font-bold text-text mb-2">{step.title}</h3>
                <p className="text-text-secondary">{step.desc}</p>
                
                {index < 2 && (
                  <div className="absolute top-8 right-0 w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <ArrowRight size={20} className="text-primary" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent rides / CTA */}
      <section className="container px-4 pb-16 md:pb-24">
        <div className="card bg-gradient-to-br from-primary to-primary-dark text-white">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-3">Prêt pour votre première livraison ?</h2>
            <p className="text-primary-100 mb-6 max-w-md mx-auto">Rejoignez des milliers de clients satisfaits à Ouagadougou</p>
            <Link to="/new" className="btn btn-lg px-10 bg-white text-primary hover:bg-gray-100 shadow-lg">
              Créer ma première course <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Trust indicators */}
      <section className="container px-4 pb-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {trustItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <div key={index} className="text-center p-4 animate-fade-in" style={{animationDelay: `${index * 100}ms`}}>
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3 text-primary">
                  <Icon size={24} />
                </div>
                <p className="font-semibold text-text mb-1">{item.label}</p>
                <p className="text-text-secondary text-xs">{item.desc}</p>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}